// TelcoTrack — portals a page's filter toolbar into the active app-page-header
// subtitle row (`.tt-page-header__filters`), beside the subtitle.
// Hub children (dashboard, materials) and embedded polygon tabs keep filters in their
// own template; this service moves the DOM node onto that filter row.

import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class PageHeaderFiltersService {
  private readonly slots: HTMLElement[] = [];
  private source: HTMLElement | null = null;
  private placeholder: Comment | null = null;

  private get slot(): HTMLElement | null {
    return this.slots[this.slots.length - 1] ?? null;
  }

  registerSlot(slot: HTMLElement): void {
    const existing = this.slots.indexOf(slot);
    if (existing >= 0) this.slots.splice(existing, 1);
    this.restore();
    this.slots.push(slot);
    this.tryMove();
  }

  unregisterSlot(slot: HTMLElement): void {
    const idx = this.slots.indexOf(slot);
    if (idx < 0) return;
    const wasActive = idx === this.slots.length - 1;
    if (wasActive) this.restore();
    this.slots.splice(idx, 1);
    if (wasActive) this.tryMove();
  }

  attach(source: HTMLElement): void {
    if (this.source && this.source !== source) {
      this.restore();
    }
    this.source = source;
    this.tryMove();
  }

  detach(source: HTMLElement): void {
    if (this.source !== source) return;
    this.restore();
    this.source = null;
  }

  private tryMove(): void {
    if (!this.slot || !this.source) return;
    if (this.source.parentElement === this.slot) return;
    this.placeholder = document.createComment('tt-page-header-filters');
    this.source.parentNode?.insertBefore(this.placeholder, this.source);
    this.slot.appendChild(this.source);
    this.slot.closest('.tt-page-header__subtitle-row')?.classList.add('tt-page-header__subtitle-row--live');
  }

  private restore(): void {
    const slot = this.source?.parentElement ?? null;
    if (!this.source || !this.placeholder?.parentNode) {
      this.placeholder = null;
      return;
    }
    this.placeholder.parentNode.insertBefore(this.source, this.placeholder);
    this.placeholder.remove();
    this.placeholder = null;
    if (!slot) return;
    const row = slot.closest('.tt-page-header__subtitle-row');
    if (!row) return;
    const tools = row.querySelector('.tt-page-header__table-tools');
    const filters = row.querySelector('.tt-page-header__filters');
    const occupied = (tools?.childElementCount ?? 0) > 0 || (filters?.childElementCount ?? 0) > 0;
    if (!occupied) row.classList.remove('tt-page-header__subtitle-row--live');
  }
}
